import React from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  createTheme,
  ThemeProvider,
} from "@mui/material"
import { v4 as uuidv4 } from "uuid"
import {
  booleanOptions,
  fileFormatOptions,
  MAPPING_FOR_PARAMETER,
  maxWidth,
  minWidth,
} from "../util/StaticData"
import { INewPipeLineColumn } from "../util/interface"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { theme as baseTheme } from "../util/CustomTheme"
import { ErrorBoundary } from "react-error-boundary"
import { CustomErrorMsg } from "./CustomErrorMsg"
import SingleSelection from "../components/Filters/SingleSelection"
import { createUseStyles } from "react-jss"
import {
  NEW_PIPELINE_CONFIG,
  newPipeLineTypes,
} from "../util/NewPipelineConfig"

export const themeMui = createTheme({
  ...baseTheme,
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 12,
          height: 30,
          width: minWidth,
        },
        input: {
          paddingRight: 4,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: 13,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: 4,
        },
      },
    },
  },
})

export const themeMuiForTextField = createTheme({
  ...baseTheme,
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 13,
          height: 26,
        },
      },
    },
  },
})

const useStyles = createUseStyles({
  centeredContent: {
    display: "flex",
    justifyContent: "center",
  },
  tableContainer: {
    maxHeight: 440,
  },
})

const getCellType = (col: any, row: any): string => {
  if (col.type === "dynamicCell") {
    const paramName = row["param_name"]
    return MAPPING_FOR_PARAMETER[paramName] || newPipeLineTypes.textDisplay
  }
  return col.type
}

const NewPipeLineConfigTable = ({ cellChange, data, activeTab }: any) => {
  const classes = useStyles()

  const renderTableCell = (
    type: any,
    value: any,
    row: any,
    rowIndex: number,
    colKey: string,
    col: any,
  ) => {
    switch (type) {
      case newPipeLineTypes.dropDownWithBools:
        return (
          <TableCell key={uuidv4()}>
            <div className={classes.centeredContent}>
              <SingleSelection
                showLabel={false}
                minWidth={col.minWidth || minWidth}
                maxWidth={col.maxWidth || maxWidth}
                changeFunc={(val: any) =>
                  cellChange({
                    value: val,
                    table: "",
                    rowIndex,
                    keyName: colKey,
                  })
                }
                filterValue={value}
                options={booleanOptions}
              />
            </div>
          </TableCell>
        )

      case newPipeLineTypes.dropDownWithFileTypes:
        return (
          <TableCell key={uuidv4()}>
            <div className={classes.centeredContent}>
              <SingleSelection
                showLabel={false}
                removeMarginRight={true}
                minWidth={col.minWidth || minWidth}
                maxWidth={col.maxWidth || maxWidth}
                changeFunc={(val: any) =>
                  cellChange({
                    value: val,
                    table: "",
                    rowIndex,
                    keyName: colKey,
                  })
                }
                filterValue={value}
                options={fileFormatOptions}
              />
            </div>
          </TableCell>
        )

      case newPipeLineTypes.textInput:
        return (
          <TableCell sx={{ maxWidth: "150px" }} key={uuidv4()}>
            <ThemeProvider theme={themeMuiForTextField}>
              <TextField
                label=""
                id="outlined-size-small"
                size="small"
                value={value}
                onChange={(event: any) =>
                  cellChange({
                    value: event.target.value,
                    table: "",
                    rowIndex,
                    keyName: colKey,
                  })
                }
              />
            </ThemeProvider>
          </TableCell>
        )

      case newPipeLineTypes.inputWithDate:
        return (
          <TableCell key={uuidv4()}>
            <ThemeProvider theme={themeMui}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={value ? new Date(value) : null}
                  format="yyyy-MM"
                  views={
                    col.type === "dynamicCell"
                      ? ["month", "year"]
                      : ["year", "month", "day"]
                  }
                  onChange={(dateValue: any) =>
                    cellChange({
                      value: dateValue,
                      table: "",
                      rowIndex,
                      keyName: colKey,
                    })
                  }
                  onError={(error) => console.error(error)}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </TableCell>
        )

      default:
        return <TableCell key={uuidv4()}>{value}</TableCell>
    }
  }

  return (
    <div>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <ErrorBoundary fallback={<CustomErrorMsg />}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader>
              {/* Table Header */}
              <TableHead>
                <TableRow>
                  {NEW_PIPELINE_CONFIG[activeTab].map(
                    (obj: INewPipeLineColumn) => {
                      return (
                        <TableCell sx={{ minWidth: "150px" }} key={obj.key}>
                          <b>{obj.label}</b>
                        </TableCell>
                      )
                    },
                  )}
                </TableRow>
              </TableHead>

              {/* Table Body */}
              <TableBody>
                {data?.length > 0 &&
                  data.map((row: any, rowIndex: number) => (
                    <TableRow key={uuidv4()}>
                      {NEW_PIPELINE_CONFIG[activeTab].map((col: any) => {
                        const value = row[col.key]
                        const cellType = getCellType(col, row)

                        return renderTableCell(
                          cellType,
                          value,
                          row,
                          rowIndex,
                          col.key,
                          col,
                        )
                      })}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </ErrorBoundary>
      </Paper>
    </div>
  )
}

export default NewPipeLineConfigTable
